import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useFeesBySolutionContext } from 'sonata-platform-components';
import { ButtonUI, ModalUI, Notifications, showNotification } from 'sonata-react-components';
import { getBidMargins, getNewBidModels, isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { BidModelInterface, PricingModelEnum } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';
import { MediaTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { ReuseIOConfiguration, StepFour, StepOne, StepThree, StepTwo } from '../Steps';
import { EntitySelectorStep } from './EntitySelectorStep';
import { FeesBySolutionWizardStyled } from './FeesBySolutionWizard.styled';

type ModalUIProps = React.ComponentProps<typeof ModalUI>;

const SELECTOR_STEP_INDEX = -1;

type Props = {
  isLoading?: boolean;
  isModalOpened: boolean;
  isSelectorDataLoading?: boolean;
  onClose: () => void;
  onFinish: (campaign: Record<string, any>) => void;
  onSelectorSelectionChange?: (campaignPlanId?: string, insertionOrderId?: string) => void;
  showSelectorStep?: boolean;
};

type State = {
  step: number;
};

export default function FeesBySolutionWizard({
  isLoading,
  isModalOpened,
  isSelectorDataLoading,
  onClose,
  onFinish,
  onSelectorSelectionChange,
  showSelectorStep
}: Props) {
  const { formatMessage } = useIntl();
  const { campaign, isStepValid, mode, solutionsAndPlansDataIsLoading, user } = useFeesBySolutionContext();

  const isNewInsertionOrderLineItem = (campaign.insertionOrder && mode === 'create') || showSelectorStep;

  const [{ step }, setState] = useState<State>({ step: 0 });
  const [selectorInsertionOrderId, setSelectorInsertionOrderId] = useState<string | undefined>();
  const [selectorCampaignPlanId, setSelectorCampaignPlanId] = useState<string | undefined>();

  const modalRef: any = useRef();

  const handleSelectorSelectionChange = (campaignPlanId?: string, insertionOrderId?: string) => {
    setSelectorCampaignPlanId(campaignPlanId);
    setSelectorInsertionOrderId(insertionOrderId);
    onSelectorSelectionChange?.(campaignPlanId, insertionOrderId);
  };

  useEffect(() => {
    if (isModalOpened) {
      modalRef?.current?.openModal?.();
    } else {
      modalRef?.current?.closeModal?.();
    }
  }, [isModalOpened]);

  const stepsOptions: ModalUIProps['steps'] = [
    {
      component: <ReuseIOConfiguration />,
      description: formatMessage({
        defaultMessage: "Choose which settings you'd like to reuse when creating this Line Item.",
        id: 'feesBySolutionWizard.reuseIOConfiguration.wizardDescription'
      }),
      title: formatMessage({
        defaultMessage: 'Line Item creation',
        id: 'feesBySolutionWizard.entitySelector.wizardTitle'
      })
    },
    {
      component: <StepOne />,
      description: formatMessage({
        defaultMessage: 'Confirm the Media channel for your Line Item.',
        id: 'feesBySolutionWizard.stepOne.wizardDescription'
      }),
      title: formatMessage({
        defaultMessage: 'Line Item creation',
        id: 'feesBySolutionWizard.entitySelector.wizardTitle'
      })
    },
    {
      component: <StepTwo />,
      description: formatMessage({
        defaultMessage: 'Select a solution and planning type for your Line Item.',
        id: 'feesBySolutionWizard.stepTwo.wizardDescription'
      }),
      title: formatMessage({
        defaultMessage: 'Line Item creation',
        id: 'feesBySolutionWizard.entitySelector.wizardTitle'
      })
    },
    {
      component: <StepThree />,
      description: formatMessage({
        defaultMessage: 'Confirm your invoice for your Line Item.',
        id: 'feesBySolutionWizard.stepThree.wizardDescription'
      }),
      title: formatMessage({
        defaultMessage: 'Line Item creation',
        id: 'feesBySolutionWizard.entitySelector.wizardTitle'
      })
    },
    {
      component: <StepFour />,
      description: formatMessage({
        defaultMessage: 'Configure the main settings for your Line Item.',
        id: 'feesBySolutionWizard.stepFour.wizardDescription'
      }),
      title: formatMessage({
        defaultMessage: 'Line Item creation',
        id: 'feesBySolutionWizard.entitySelector.wizardTitle'
      })
    }
  ];

  const selectorStepOption: NonNullable<ModalUIProps['steps']>[number] = {
    component: <EntitySelectorStep onSelectionChange={handleSelectorSelectionChange} />,
    description: formatMessage({
      defaultMessage: 'Select the associated Advertiser, Campaign Plan and Insertion Order to inherit their settings.',
      id: 'feesBySolutionWizard.entitySelector.wizardDescription'
    }),
    title: formatMessage({
      defaultMessage: 'Line Item creation',
      id: 'feesBySolutionWizard.entitySelector.wizardTitle'
    })
  };

  const getCampaignFeesType = (): 'legacyFees' | 'feesBySolution' => {
    const campaignAlreadyCreated = !!campaign._id;
    const campaignFeeBySolutionEnabled = campaign?.solutionsFees?.enabled;
    const userFeesBySolutionEnabled = user?.solutionsFees?.enabled;
    if (campaignAlreadyCreated && campaignFeeBySolutionEnabled) {
      return 'feesBySolution';
    }
    if (!campaignAlreadyCreated && userFeesBySolutionEnabled) {
      return 'feesBySolution';
    }
    return 'legacyFees';
  };

  const getStepNumbersByUser = (): number[] => {
    const campaignFeesType = getCampaignFeesType();
    let baseSteps: number[];
    if (campaignFeesType === 'feesBySolution') {
      baseSteps = Array.from(
        {
          length: isNewInsertionOrderLineItem ? stepsOptions.length : stepsOptions.length - 1
        },
        (_, index) => (isNewInsertionOrderLineItem ? index : index + 1)
      );
    } else {
      baseSteps = isNewInsertionOrderLineItem ? [0, 1, 4] : [1, 4];
    }
    return showSelectorStep ? [SELECTOR_STEP_INDEX, ...baseSteps] : baseSteps;
  };

  const stepsNumbers = getStepNumbersByUser();
  const steps = stepsNumbers.map(n => (n === SELECTOR_STEP_INDEX ? selectorStepOption : stepsOptions[n]));

  const isNextButtonDisabled = () => {
    if (stepsNumbers[step] === SELECTOR_STEP_INDEX) {
      return !selectorCampaignPlanId || !selectorInsertionOrderId || !!isSelectorDataLoading;
    }
    return !isStepValid(stepsNumbers[step]).valid || solutionsAndPlansDataIsLoading;
  };

  const checkRequireFields = () => {
    if (stepsNumbers[step] === SELECTOR_STEP_INDEX) {
      return true;
    }
    const { error, valid } = isStepValid(stepsNumbers[step]);
    if (error === 'iabCategories') {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Campaign Category is required',
          id: 'feesBySolution.errorIABCategoriesSelected'
        })
      );
    }
    if (error === 'attributionModelValue') {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'The range of values ​​of the attribution model is not valid',
          id: 'feesBySolutionWizard.errorAttributionModelValue'
        })
      );
    }
    if (error === 'countries') {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Campaign Countries is required',
          id: 'feesBySolutionWizard.errorCountriesSelected'
        })
      );
    }
    if (error === 'mediaAdvertiser') {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Campaign Media Advertiser is required',
          id: 'feesBySolutionWizard.errorMediaAdvertiserFilled'
        })
      );
    }
    if (error === 'mediaAdvertiserDooh') {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Campaign Advertiser is required',
          id: 'feesBySolutionWizard.errorMediaAdvertiserDOOH'
        })
      );
    }
    if (error === 'mastercardProvider') {
      showNotification(
        'error',
        formatMessage({
          defaultMessage:
            'Mastercard variable is required and the percentage is greater than 0 and less than or equal to 100',
          id: 'feesBySolutionWizard.errorMastercard'
        })
      );
    }
    if (error === 'billingSubsidiary') {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Billing Subsidiary is required',
          id: 'feesBySolutionWizard.errorBillingSubsidiary'
        })
      );
    }
    return valid;
  };

  const handleFinish = () => {
    if (checkRequireFields()) {
      let newBidModels: BidModelInterface[] = [];
      let newBidMargins: Record<string, number> = {};
      if (isDOOHCampaign(campaign.type)) {
        const { bidMargin: userBidMargins, marketplaceDistribution, minimumBidPrices: userMinimumBidPrices } = user;
        newBidModels = getNewBidModels(
          MediaTypeEnum.pmp,
          PricingModelEnum['f-cpm'],
          userMinimumBidPrices,
          marketplaceDistribution
        );
        newBidMargins = getBidMargins(MediaTypeEnum.pmp, userBidMargins);
      }
      onFinish({
        ...campaign,
        name:
          campaign.name ||
          formatMessage(
            {
              defaultMessage: `My campaign ${moment().format('YYYY-MM-DD HH:mm')}`,
              id: 'feesBySolution.myCampaignName'
            },
            { date: moment().format('YYYY-MM-DD HH:mm') }
          ),
        ...(mode === 'create' && {
          solutionsFees: {
            ...campaign.solutionsFees,
            // @ts-ignore
            enabled: user.solutionsFees?.enabled
          },
          ...(isDOOHCampaign(campaign.type) && {
            bidModels: newBidModels,
            margin: newBidMargins,
            mediaType: MediaTypeEnum.pmp
          })
        })
      });
    }
  };

  const handleStepChange = (newStep: number) => {
    setState(prevState => ({
      ...prevState,
      step: newStep
    }));
  };

  const finishButton = {
    id: 'feesBySolutionWizard-finishButton',
    loading: isLoading,
    onClick: handleFinish,
    text:
      mode === 'create'
        ? formatMessage({
            defaultMessage: 'Create Line Item',
            id: 'feesBySolutionWizard.finishButton'
          })
        : formatMessage({
            defaultMessage: 'Update Line Item',
            id: 'feesBySolutionWizard.updateButton'
          })
  };

  const handleCloseButton = () => {
    onClose();
  };

  function LeftButton() {
    return (
      <ButtonUI
        onClick={handleCloseButton}
        shape="squareRounded"
        text={formatMessage({
          defaultMessage: 'Cancel',
          id: 'common.cancel'
        })}
        type="secondary"
      />
    );
  }

  return (
    <>
      <Notifications portalId="feesBySolutionNotifications" />
      <FeesBySolutionWizardStyled className="feesBySolutionWizard" data-testid={'feesBySolutionWizard'}>
        <ModalUI
          closeModalCallback={handleCloseButton}
          disabledNextButton={isNextButtonDisabled()}
          finishButton={finishButton}
          leftButton={<LeftButton />}
          onStepChange={handleStepChange}
          ref={modalRef}
          steps={steps}
          title=""
          width="958px"
        />
      </FeesBySolutionWizardStyled>
    </>
  );
}
